import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { fetchManualeGiochi } from '../utils/manualeGiochiApi';
import {
  CATEGORIE_GIOCO,
  COMPETITIVITA,
  FASCE_ETA,
  LIVELLI,
  MATERIALI,
  MECCANICHE_GIOCO,
  OBIETTIVI_EDUCATIVI,
  SPAZI_GIOCO,
  TIPOLOGIA_CARD_BORDER_COLOR,
  TIPOLOGIA_COLOR_SCHEME,
  TIPOLOGIE,
  etaMinDaFasceEta,
} from '../constants/manualeGiocoFilters';

const tipologiaLabel = (v) =>
  TIPOLOGIE.find((t) => t.value === v)?.label || v || '';

const MultiCheckRow = ({ label, options, value, onChange }) => (
  <FormControl>
    <FormLabel fontSize="sm">{label}</FormLabel>
    <CheckboxGroup value={value} onChange={onChange}>
      <Wrap spacing={2}>
        {options.map((opt) => (
          <WrapItem key={opt}>
            <Checkbox size="sm" value={opt}>
              {opt}
            </Checkbox>
          </WrapItem>
        ))}
      </Wrap>
    </CheckboxGroup>
  </FormControl>
);

const GiocoCard = ({ entry }) => {
  const slug = entry.slug || entry.documentId;
  const tip = entry.tipologia;
  const borderAccent =
    tip && TIPOLOGIA_CARD_BORDER_COLOR[tip]
      ? TIPOLOGIA_CARD_BORDER_COLOR[tip]
      : null;
  const badgeScheme =
    tip && TIPOLOGIA_COLOR_SCHEME[tip] ? TIPOLOGIA_COLOR_SCHEME[tip] : 'teal';
  const fallbackBorder = useColorModeValue('gray.100', 'gray.600');
  const etaMinTag = etaMinDaFasceEta(entry.fasceEta);

  return (
    <Stack
      as={Link}
      to={`/manuale-giochi/${encodeURIComponent(slug)}`}
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="md"
      borderWidth={borderAccent ? '3px' : '1px'}
      borderStyle="solid"
      borderColor={borderAccent || fallbackBorder}
      _hover={{
        boxShadow: 'lg',
        transform: 'translateY(-2px)',
      }}
      transition="border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease"
      textDecoration="none"
      color="inherit"
      p={4}
      spacing={2}
      minW={0}
    >
      <Heading size="sm" noOfLines={2}>
        {entry.titolo}
      </Heading>
      {(entry.tipologia || entry.durataMinuti != null || etaMinTag != null) && (
        <HStack
          w="100%"
          justify="space-between"
          align="center"
          spacing={2}
          flexWrap="wrap"
          gap={1}
        >
          <Wrap flex="1" minW={0} spacing={1} shouldWrapChildren>
            {entry.tipologia ? (
              <Badge colorScheme={badgeScheme} w="fit-content">
                {tipologiaLabel(entry.tipologia)}
              </Badge>
            ) : null}
            {etaMinTag != null ? (
              <Badge variant="subtle" colorScheme="blue" fontSize="0.65rem">
                Da {etaMinTag} anni
              </Badge>
            ) : null}
          </Wrap>
          {entry.durataMinuti != null ? (
            <Text
              fontSize="xs"
              color="gray.600"
              fontWeight="semibold"
              flexShrink={0}
            >
              Min. {entry.durataMinuti} min
            </Text>
          ) : null}
        </HStack>
      )}
      {entry.descrizioneBreve && (
        <Text fontSize="sm" color="gray.600" noOfLines={3}>
          {entry.descrizioneBreve}
        </Text>
      )}
    </Stack>
  );
};

function ManualeGiochi() {
  const { isAuthenticated } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [tipologia, setTipologia] = useState('');
  const [competitivita, setCompetitivita] = useState('');
  const [energia, setEnergia] = useState('');
  const [complessita, setComplessita] = useState('');
  const [rischio, setRischio] = useState('');
  const [durataMin, setDurataMin] = useState('');
  const [durataMax, setDurataMax] = useState('');
  const [categorieGioco, setCategorieGioco] = useState([]);
  const [meccanicheGioco, setMeccanicheGioco] = useState([]);
  const [fasceEta, setFasceEta] = useState([]);
  const [spaziGioco, setSpaziGioco] = useState([]);
  const [obiettiviEducativi, setObiettiviEducativi] = useState([]);
  const [materiali, setMateriali] = useState([]);

  const countBannerBg = useColorModeValue('teal.50', 'whiteAlpha.100');
  const countBannerBorder = useColorModeValue('teal.200', 'teal.600');
  const countNumberColor = useColorModeValue('teal.700', 'teal.200');
  const countLabelColor = useColorModeValue('gray.800', 'gray.100');
  const countSubColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: 12,
      q: qDebounced || undefined,
      tipologia: tipologia || undefined,
      competitivita: competitivita || undefined,
      energia: energia || undefined,
      complessita: complessita || undefined,
      rischio: rischio || undefined,
      durataMin: durataMin || undefined,
      durataMax: durataMax || undefined,
      categorieGioco: categorieGioco.length ? categorieGioco : undefined,
      meccanicheGioco: meccanicheGioco.length ? meccanicheGioco : undefined,
      fasceEta: fasceEta.length ? fasceEta : undefined,
      spaziGioco: spaziGioco.length ? spaziGioco : undefined,
      obiettiviEducativi: obiettiviEducativi.length
        ? obiettiviEducativi
        : undefined,
      materiali: materiali.length ? materiali : undefined,
    }),
    [
      page,
      qDebounced,
      tipologia,
      competitivita,
      energia,
      complessita,
      rischio,
      durataMin,
      durataMax,
      categorieGioco,
      meccanicheGioco,
      fasceEta,
      spaziGioco,
      obiettiviEducativi,
      materiali,
    ]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMeta(null);
    try {
      const json = await fetchManualeGiochi(filters);
      setRows(json.data || []);
      setMeta(json.meta || null);
    } catch (e) {
      setError(e.message || 'Errore');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [
    qDebounced,
    tipologia,
    competitivita,
    energia,
    complessita,
    rischio,
    durataMin,
    durataMax,
    categorieGioco,
    meccanicheGioco,
    fasceEta,
    spaziGioco,
    obiettiviEducativi,
    materiali,
  ]);

  const resetFilters = () => {
    setQ('');
    setTipologia('');
    setCompetitivita('');
    setEnergia('');
    setComplessita('');
    setRischio('');
    setDurataMin('');
    setDurataMax('');
    setCategorieGioco([]);
    setMeccanicheGioco([]);
    setFasceEta([]);
    setSpaziGioco([]);
    setObiettiviEducativi([]);
    setMateriali([]);
  };

  const pagination = meta?.pagination;
  const pageCount = pagination?.pageCount || 1;
  const totalGames =
    !error && pagination?.total != null ? pagination.total : null;

  const gamesCountLine =
    loading && totalGames == null ? (
      <HStack
        mb={3}
        px={3}
        py={2.5}
        borderRadius="lg"
        bg={countBannerBg}
        borderWidth="1px"
        borderColor={countBannerBorder}
        spacing={3}
      >
        <Spinner size="sm" thickness="3px" color="teal.500" />
        <Text fontSize="sm" fontWeight="semibold" color={countLabelColor}>
          Caricamento…
        </Text>
      </HStack>
    ) : totalGames != null ? (
      <Box
        mb={3}
        px={3.5}
        py={3}
        borderRadius="lg"
        bg={countBannerBg}
        borderWidth="1px"
        borderColor={countBannerBorder}
        boxShadow="sm"
      >
        <HStack spacing={2.5} align="baseline" flexWrap="wrap">
          <Text
            as="span"
            fontSize="2xl"
            fontWeight="extrabold"
            lineHeight="1"
            color={countNumberColor}
            letterSpacing="-0.02em"
          >
            {totalGames}
          </Text>
          <Text
            as="span"
            fontSize="md"
            fontWeight="semibold"
            color={countLabelColor}
          >
            {totalGames === 1 ? 'gioco disponibile' : 'giochi disponibili'}
          </Text>
        </HStack>
        <Text fontSize="xs" color={countSubColor} mt={1.5} lineHeight="short">
          In base ai filtri attivi
        </Text>
      </Box>
    ) : null;

  const filtersForm = (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel fontSize="sm">Cerca</FormLabel>
        <Input
          size="sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Titolo…"
        />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="sm">Tipologia</FormLabel>
        <Select
          size="sm"
          value={tipologia}
          onChange={(e) => setTipologia(e.target.value)}
          placeholder="Tutte"
        >
          {TIPOLOGIE.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="sm">Competitività</FormLabel>
        <Select
          size="sm"
          value={competitivita}
          onChange={(e) => setCompetitivita(e.target.value)}
          placeholder="Tutte"
        >
          {COMPETITIVITA.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </FormControl>
      <HStack>
        <FormControl>
          <FormLabel fontSize="sm">Energia</FormLabel>
          <Select
            size="sm"
            value={energia}
            onChange={(e) => setEnergia(e.target.value)}
            placeholder="-"
          >
            {LIVELLI.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Complessità</FormLabel>
          <Select
            size="sm"
            value={complessita}
            onChange={(e) => setComplessita(e.target.value)}
            placeholder="-"
          >
            {LIVELLI.map((t) => (
              <option key={`c-${t.value}`} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormControl>
      </HStack>
      <FormControl>
        <FormLabel fontSize="sm">Rischio</FormLabel>
        <Select
          size="sm"
          value={rischio}
          onChange={(e) => setRischio(e.target.value)}
          placeholder="-"
        >
          {LIVELLI.map((t) => (
            <option key={`r-${t.value}`} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </FormControl>
      <HStack>
        <FormControl>
          <FormLabel fontSize="sm">Durata min (min)</FormLabel>
          <Input
            size="sm"
            type="number"
            min={0}
            value={durataMin}
            onChange={(e) => setDurataMin(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Durata max</FormLabel>
          <Input
            size="sm"
            type="number"
            min={0}
            value={durataMax}
            onChange={(e) => setDurataMax(e.target.value)}
          />
        </FormControl>
      </HStack>
      <Divider />
      <MultiCheckRow
        label="Categorie"
        options={CATEGORIE_GIOCO}
        value={categorieGioco}
        onChange={setCategorieGioco}
      />
      <MultiCheckRow
        label="Meccaniche"
        options={MECCANICHE_GIOCO}
        value={meccanicheGioco}
        onChange={setMeccanicheGioco}
      />
      <MultiCheckRow
        label="Fasce età"
        options={FASCE_ETA}
        value={fasceEta}
        onChange={setFasceEta}
      />
      <MultiCheckRow
        label="Spazio"
        options={SPAZI_GIOCO}
        value={spaziGioco}
        onChange={setSpaziGioco}
      />
      <MultiCheckRow
        label="Obiettivi educativi"
        options={OBIETTIVI_EDUCATIVI}
        value={obiettiviEducativi}
        onChange={setObiettiviEducativi}
      />
      <MultiCheckRow
        label="Materiali"
        options={MATERIALI}
        value={materiali}
        onChange={setMateriali}
      />
      <Button size="sm" variant="outline" onClick={resetFilters}>
        Azzera filtri
      </Button>
    </VStack>
  );

  return (
    <Container maxW="7xl" py={{ base: 6, md: 10 }}>
      <Helmet>
        <title>Manuale giochi | CIL</title>
      </Helmet>
      <Flex
        justify="space-between"
        align="flex-start"
        gap={4}
        mb={8}
        flexWrap="wrap"
        bg="white"
        p={{ base: 4, md: 5 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.100"
      >
        <Box flex="1" minW={0}>
          <Heading size="xl" mb={2}>
            Manuale giochi
          </Heading>
          <Text color="gray.600">
            Schede di animazione pubblicate da CIL. Usa i filtri per trovare
            giochi per età, spazio o tipologia.
          </Text>
        </Box>
        {isAuthenticated ? (
          <Button
            as={Link}
            to="/manuale-giochi/nuovo"
            colorScheme="yellow"
            size="md"
            flexShrink={0}
          >
            Aggiungi un gioco
          </Button>
        ) : null}
      </Flex>

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        gap={8}
        align="flex-start"
      >
        <Box display={{ base: 'block', lg: 'none' }} w="100%">
          {gamesCountLine}
          <Accordion allowToggle defaultIndex={[]} reduceMotion>
            <AccordionItem
              bg="white"
              borderWidth="1px"
              borderColor="gray.100"
              borderRadius="lg"
              overflow="hidden"
            >
              <AccordionButton px={4} py={3} _expanded={{ bg: 'gray.50' }}>
                <Text as="span" flex="1" textAlign="left" fontWeight="bold">
                  Filtri
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={4} pb={4} pt={0}>
                {filtersForm}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        <Box
          display={{ base: 'none', lg: 'block' }}
          flexShrink={0}
          w={{ base: '100%', lg: '280px' }}
          bg="white"
          p={4}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.100"
        >
          {gamesCountLine}
          <Text fontWeight="bold" mb={3}>
            Filtri
          </Text>
          {filtersForm}
        </Box>

        <Box flex="1" minW={0}>
          {error && (
            <Text color="red.500" mb={4}>
              {error}
            </Text>
          )}
          {loading ? (
            <Flex justify="center" py={16}>
              <Spinner size="lg" />
            </Flex>
          ) : rows.length === 0 ? (
            <Text color="gray.600">
              Nessun gioco trovato con questi filtri.
            </Text>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
                {rows.map((entry, i) => (
                  <GiocoCard
                    key={entry.documentId || entry.slug || i}
                    entry={entry}
                  />
                ))}
              </SimpleGrid>
              {pageCount > 1 && (
                <HStack mt={8} justify="center" spacing={4}>
                  <Button
                    size="sm"
                    isDisabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Precedente
                  </Button>
                  <Text fontSize="sm" color="gray.600">
                    Pagina {page} di {pageCount}
                    {pagination?.total != null
                      ? ` (${pagination.total} giochi)`
                      : ''}
                  </Text>
                  <Button
                    size="sm"
                    isDisabled={page >= pageCount}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Successiva
                  </Button>
                </HStack>
              )}
            </>
          )}
        </Box>
      </Flex>
    </Container>
  );
}

export default ManualeGiochi;
